import type {
	CustomAttributes,
	FrontendExperimentsResult,
	Identifiers,
} from '@atlaskit/feature-gate-js-client/types';
import {
	createGateEnvelopePublisher,
	postGateEnvelopeToExtensionPage,
	type GateEnvelope,
} from '@rovo-extension/feature-gates-adapter';

import { featureGatesOptions, pollingProvider } from '../bootstrap/featureGateProvider';
import { extensionUrl } from '../config';
import { logError } from '../utils/logger';

export interface GateProfile {
	readonly identifiers: Identifiers;
	readonly customAttributes: CustomAttributes;
}

/**
 * Frontend wiring for the chrome-layer envelope producer in `@rovo-extension/feature-gates-adapter`.
 * Everything specific to this package lives here: the polling provider that owns the raw values
 * blob, the target app, the extension origin the envelope is posted to, and the logger.
 */
const publish = createGateEnvelopePublisher({
	targetApp: featureGatesOptions.targetApp,
	getValues: () => pollingProvider.getExperimentValues(),
	post: (message) => postGateEnvelopeToExtensionPage(message, extensionUrl),
	onError: (error, impact) => logError({ error, impact }),
});

/**
 * Publishes the current gate values to the extension page hosting this iframe. Pass `update` when
 * called from the provider's `applyUpdateCallback`; omit it on first load, where that callback does
 * not fire and the blob has to be read explicitly.
 */
export function publishGateEnvelope(
	profile: GateProfile,
	update?: FrontendExperimentsResult,
): Promise<GateEnvelope | undefined> {
	return publish(profile, update);
}
